// src/pages/query-log/QueryLogDetailDrawer.tsx
// Right-side drawer for one query-log row: request payload (SQL highlighted),
// the best-available stored result rendered tool-aware, and — for read-only
// tools — a live "Re-run" that replays the same request under the current
// user's access. Lazy-loads the full payload only when opened.

import React from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { AlertCircle, RotateCw } from 'lucide-react';
import Drawer from '@/components/ui/Drawer';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import MarkdownMessage from '@/components/chat/MarkdownMessage';
import { getToolDisplay } from '@/components/chat/toolDisplay';
import {
  CodeBlockSection,
  JsonSection,
  KeyValue,
  MarkdownResultSection,
  Section,
  StatusBadge,
} from '@/components/tool-calls/toolCallView';
import { formatTimestamp, getErrorMessage } from '@/components/tool-calls/toolCallFormat';
import { queryLogApi, type QueryLogDetail } from '@/services/query-log-api';

interface QueryLogDetailDrawerProps {
  id: string | null;
  onClose: () => void;
}

const QueryLogDetailDrawer: React.FC<QueryLogDetailDrawerProps> = ({ id, onClose }) => {
  const isOpen = !!id;

  const { data, isLoading, error } = useQuery({
    queryKey: ['queryLogDetail', id],
    queryFn: () => queryLogApi.getDetail(id!),
    enabled: isOpen,
  });

  const title = data ? getToolDisplay(data.toolName).label : 'Tool call';

  return (
    <Drawer isOpen={isOpen} onClose={onClose} title={title} width="lg">
      {isLoading && (
        <div className="flex items-center justify-center py-16">
          <LoadingSpinner size="lg" />
        </div>
      )}

      {error && (
        <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
          <AlertCircle className="w-10 h-10 text-destructive mb-3" />
          <p className="text-sm font-medium text-foreground mb-1">Couldn't load tool call</p>
          <p className="text-xs text-muted-foreground">{getErrorMessage(error)}</p>
        </div>
      )}

      {data && <DetailBody detail={data} />}
    </Drawer>
  );
};

const DetailBody: React.FC<{ detail: QueryLogDetail }> = ({ detail }) => {
  const { icon: Icon } = getToolDisplay(detail.toolName);
  const isSuccess = detail.status === 'success';

  return (
    <div className="px-5 py-4 space-y-5">
      {/* Tool + status */}
      <div className="flex items-center justify-between gap-3 pb-4 border-b border-border">
        <div className="flex items-center gap-2 min-w-0">
          <Icon className="w-4 h-4 text-muted-foreground flex-shrink-0" />
          <code className="text-xs font-mono text-muted-foreground truncate">
            {detail.toolName}
          </code>
        </div>
        <StatusBadge status={detail.status} />
      </div>

      {/* Access */}
      <Section title="Access">
        <KeyValue
          label="Initiated by"
          value={
            detail.initiatedBy.name +
            (detail.initiatedBy.sub ? ` · ${detail.initiatedBy.sub}` : '')
          }
        />
        <KeyValue label="Via" value={detail.via.label} />
        <KeyValue label="Source" value={detail.sourceName ?? '—'} />
        <KeyValue label="Account" value={detail.accountName ?? '—'} />
      </Section>

      {/* Execution */}
      <Section title="Execution">
        <KeyValue label="Started" value={formatTimestamp(detail.at)} />
        <KeyValue
          label="Duration"
          value={detail.tookMs != null ? `${detail.tookMs.toLocaleString()} ms` : '—'}
        />
        <KeyValue
          label="Rows"
          value={detail.rowCount != null ? detail.rowCount.toLocaleString() : '—'}
        />
      </Section>

      {/* Error / denial */}
      {!isSuccess && (detail.errorMessage || detail.errorCode) && (
        <Section title={detail.denied ? 'Denied' : 'Error'}>
          {detail.errorCode && <KeyValue label="Code" value={detail.errorCode} />}
          {detail.errorMessage && (
            <pre className="mt-2 text-xs text-destructive bg-destructive/15 rounded-md p-3 whitespace-pre-wrap break-words">
              {detail.errorMessage}
            </pre>
          )}
        </Section>
      )}

      {/* Request */}
      <RequestView payload={detail.requestPayload} />

      {/* Stored result */}
      <section>
        {detail.result.source === 'summary' && (
          <p className="text-xs text-muted-foreground mb-2 italic">
            The full result isn't retained for this tool — only a summary was stored.
            {detail.canRerun && ' Re-run below to see the live result.'}
          </p>
        )}
        {detail.result.value != null ? (
          <ToolResultView toolName={detail.toolName} value={detail.result.value} />
        ) : (
          <>
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
              Result
            </h3>
            <p className="text-sm text-muted-foreground italic">
              No result was stored for this tool call.
              {detail.canRerun && ' Re-run to fetch it live.'}
            </p>
          </>
        )}
      </section>

      {/* Live re-run */}
      {detail.canRerun && <RerunBlock id={detail.id} toolName={detail.toolName} />}
    </div>
  );
};

/** Request payload — SQL pulled out as highlighted code, then the raw args. */
const RequestView: React.FC<{ payload: Record<string, unknown> | null }> = ({ payload }) => {
  const sql = payload && typeof payload.sql === 'string' ? payload.sql : null;
  const compiled = payload && typeof payload.compiled_sql === 'string' ? payload.compiled_sql : null;

  return (
    <div className="space-y-5">
      {sql && <CodeBlockSection title="Query" code={sql} language="sql" />}
      {compiled && <CodeBlockSection title="Compiled SQL" code={compiled} language="sql" />}
      <JsonSection title="Request payload" value={payload} />
    </div>
  );
};

/**
 * Renders a tool result. Special-cases get_account_schema (the schema is a big
 * newline-formatted string that JSON-escapes into an unreadable blob) into a
 * code block + markdown usage notes; renders markdown results as markdown;
 * everything else as pretty JSON (arrays included).
 */
const ToolResultView: React.FC<{ toolName: string; value: unknown; title?: string }> = ({
  toolName,
  value,
  title = 'Result',
}) => {
  const schema = toolName === 'get_account_schema' ? asSchemaResult(value) : null;
  if (schema) {
    return (
      <div className="space-y-5">
        <CodeBlockSection title="Schema" code={schema.schema} />
        {schema.usageNotes && (
          <section>
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
              Usage notes
            </h3>
            <div className="rounded-md p-3 bg-background border border-border overflow-x-auto">
              <MarkdownMessage content={schema.usageNotes} />
            </div>
          </section>
        )}
        {Object.keys(schema.rest).length > 0 && (
          <JsonSection title="Other fields" value={schema.rest} />
        )}
      </div>
    );
  }

  const markdown =
    value && typeof value === 'object' && !Array.isArray(value) &&
    typeof (value as Record<string, unknown>).markdown === 'string'
      ? ((value as Record<string, unknown>).markdown as string)
      : null;
  if (markdown) {
    return (
      <MarkdownResultSection
        markdown={markdown}
        fullPayload={value as Record<string, unknown>}
        title={title}
      />
    );
  }

  return <JsonSection title={title} value={value} />;
};

function asSchemaResult(
  value: unknown,
): { schema: string; usageNotes?: string; rest: Record<string, unknown> } | null {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const v = value as Record<string, unknown>;
    if (typeof v.schema === 'string') {
      const { schema, usage_notes, ...rest } = v;
      return {
        schema: schema as string,
        usageNotes: typeof usage_notes === 'string' ? usage_notes : undefined,
        rest,
      };
    }
  }
  return null;
}

/** Re-run the same request live and render the fresh result in place. */
const RerunBlock: React.FC<{ id: string; toolName: string }> = ({ id, toolName }) => {
  const mutation = useMutation({ mutationFn: () => queryLogApi.rerun(id) });

  return (
    <section className="rounded-lg border border-border bg-muted/40 p-4">
      <div className="flex items-center justify-between gap-3 mb-1">
        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
          Live re-run
        </h3>
        <button
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending}
          className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-ring/40"
        >
          <RotateCw className={mutation.isPending ? 'w-3.5 h-3.5 animate-spin' : 'w-3.5 h-3.5'} />
          {mutation.isPending ? 'Running…' : mutation.data ? 'Re-run again' : 'Re-run'}
        </button>
      </div>
      <p className="text-xs text-muted-foreground">
        Runs the same request now, under your own access. The fresh result isn't stored beyond
        this view.
      </p>

      {mutation.isError && (
        <div className="mt-3 text-xs text-destructive bg-destructive/15 rounded-md p-3">
          {getErrorMessage(mutation.error)}
        </div>
      )}

      {mutation.data && !mutation.data.success && (
        <div className="mt-3">
          <div className="text-[9.5px] font-bold uppercase tracking-wider text-destructive mb-1">
            Re-run failed
          </div>
          <pre className="text-xs text-destructive bg-destructive/15 rounded-md p-3 whitespace-pre-wrap break-words">
            {mutation.data.error ?? 'The tool returned no result.'}
          </pre>
        </div>
      )}

      {mutation.data && mutation.data.success && (
        <div className="mt-4">
          <div className="text-[9.5px] font-bold uppercase tracking-wider text-success mb-2">
            Live result · just now
          </div>
          <ToolResultView toolName={toolName} value={mutation.data.result} title="Live result" />
        </div>
      )}
    </section>
  );
};

export default QueryLogDetailDrawer;
